/**
 * Turns a teacher's manual score correction for ONE submission's task into a durable rubric refinement,
 * then re-grades every OTHER submission of that same task against it — including submissions whose grade
 * was already `isApproved`. Approval status does not exempt a submission from a consistency re-check (see
 * the "consistent grading" plan); the just-edited submission itself is excluded since it already reflects
 * the teacher's intent and re-grading it would immediately overwrite their edit.
 *
 * Same refine → re-grade → announce shape as the chat-driven `refine_grading_scheme` tool, and by the same route:
 * the scope rides the refine row as a `RegradeIntent` and the row dispatches it when it lands
 * (`dispatchOwnRegrade` in `gs-refine.job.ts`). What differs here is the trigger (a manual score edit, not chat
 * feedback) and the submission scope — all but one, not all.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Submission } from '../../submission.js';

export interface CaptureManualGradingInsightOptions {
  examId: string;
  /** The submission that was just manually corrected — excluded from the resulting regrade cascade. */
  submissionId: string;
  /** Qualified task ref to refine/regrade, e.g. "3" or "3a" (matches refineGradingSchemeAgent's taskNumbers). */
  taskRef: string;
  /** The top-level task number `taskRef` belongs to — used only for the previously-approved count below. */
  mainTaskNumber: string;
  oldScore: number;
  newScore: number;
  maxPoints?: number;
  oldExplanation: string;
  newExplanation?: string;
  studentAnswerExcerpt: string;
}

/** Below this delta a score "correction" is treated as no real change (rounding/re-save), not an insight. */
const MEANINGFUL_SCORE_DELTA = 0.001;

function buildInsightFeedback(opts: CaptureManualGradingInsightOptions): string {
  const maxPointsText = typeof opts.maxPoints === 'number' ? `${opts.maxPoints}` : 'unbekannt';
  return (
    `Eine Lehrkraft hat die KI-Bewertung für eine Schülerabgabe dieser Aufgabe manuell korrigiert ` +
    `(vorher: ${opts.oldScore}/${maxPointsText} Punkte, Begründung: "${opts.oldExplanation}"; ` +
    `korrigiert auf: ${opts.newScore}/${maxPointsText} Punkte` +
    (opts.newExplanation ? `, Kommentar der Lehrkraft: "${opts.newExplanation}"` : '') +
    `). Antwortauszug der korrigierten Abgabe: "${opts.studentAnswerExcerpt}". ` +
    `Leite daraus eine allgemeine Präzisierung der Bewertungskriterien ab, die künftig ähnliche Antworten ` +
    `konsistent mit dieser Korrektur bewertet, ohne die bestehenden Kriterien unnötig zu verändern.`
  );
}

/**
 * Best-effort count of how many of the OTHER submissions already had this task's main-task-level grade
 * `isApproved` before this regrade — purely informational (surfaced in the chat announcement so the
 * teacher knows which previously-signed-off submissions now need a fresh look). Checked at main-task
 * granularity even for a subtask edit: precise enough for that purpose without re-deriving the same
 * nested-path resolution `gradeOneTask` already owns.
 */
async function countPreviouslyApprovedOthers(
  examId: string,
  mainTaskNumber: string,
  otherSubmissionIds: string[],
): Promise<number> {
  if (otherSubmissionIds.length === 0) return 0;
  const submissionCollection = await getCollection(Submission);
  return submissionCollection.countDocuments({
    examId,
    _id: { $in: otherSubmissionIds },
    taskSubmissions: { $elemMatch: { taskNumber: mainTaskNumber, 'gradingResult.isApproved': true } },
  } as any);
}

export async function captureManualGradingInsightAndRegrade(opts: CaptureManualGradingInsightOptions): Promise<void> {
  const { examId, submissionId, taskRef, mainTaskNumber, oldScore, newScore } = opts;

  if (Math.abs(newScore - oldScore) < MEANINGFUL_SCORE_DELTA) return;

  try {
    const submissionCollection = await getCollection(Submission);
    const allSubmissions = await submissionCollection
      .find({ examId } as any, { projection: { _id: 1 } } as any)
      .toArray();
    const otherSubmissionIds = allSubmissions
      .map(sub => String((sub as any)._id))
      .filter(id => id && id !== submissionId);

    if (otherSubmissionIds.length === 0) return;

    // Counted NOW, before the refine runs: it describes who had signed off on this task at the moment the
    // teacher made their correction. Approval state does not change while the refine thinks, so reading it
    // here rather than after the (now asynchronous) refine gives the same number.
    const previouslyApprovedCount = await countPreviouslyApprovedOthers(examId, mainTaskNumber, otherSubmissionIds);

    // Queued, not awaited, and the re-grade travels WITH it. This used to wait for the refine and then
    // dispatch the re-grade itself, which meant two of them once the run's closure started dispatching one
    // too. The scope the closure could not have guessed — the other submissions only, content only, and the
    // approval count for the announcement — is exactly what the intent carries.
    const { dispatchJobs } = await import('../jobs/dispatch.js');
    const { REFINE_JOB_TYPE } = await import('../jobs/job.js');
    await dispatchJobs(REFINE_JOB_TYPE.gradingScheme, examId, {
      taskRefs: [taskRef],
      feedback: buildInsightFeedback(opts),
      regrade: {
        cause: 'manual_grading_insight',
        target: 'content',
        submissionIds: otherSubmissionIds,
        previouslyApprovedCount,
      },
    });
  } catch (err) {
    logger.error(
      { err, examId, submissionId, taskRef, context: 'captureManualGradingInsightAndRegrade' },
      'Manual grading insight capture/regrade failed',
    );
  }
}
